import React, { useEffect, useRef, useState } from 'react';
import { Bell, MessageSquare, FolderPlus, UserCheck, ThumbsUp, ThumbsDown, Check, X } from 'lucide-react';
import { NotificationService, NOTIF_LABELS } from '../services/notificationService';
import { convertFirestoreTimestamp } from '../utils/helpers';

const ICONS = {
  'message': { Icon: MessageSquare, cls: 'bg-brand-100 text-brand-700' },
  'dossier.nouveau': { Icon: FolderPlus, cls: 'bg-indigo-100 text-indigo-700' },
  'dossier.affectation': { Icon: UserCheck, cls: 'bg-accent-100 text-accent-700' },
  'avis.favorable': { Icon: ThumbsUp, cls: 'bg-emerald-100 text-emerald-700' },
  'avis.defavorable': { Icon: ThumbsDown, cls: 'bg-red-100 text-red-700' }
};

const relative = (ts) => {
  const d = convertFirestoreTimestamp(ts);
  if (!d) return "à l'instant";
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 172800) return 'hier';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Cloche de notifications : compteur de non-lus, panneau (menu desktop / feuille mobile),
// clic → ouverture du dossier concerné. Aucune dépendance à un backend.
const NotificationBell = ({ cabinetId, uid, isArchitect, onOpenProject }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const baseTitle = useRef(typeof document !== 'undefined' ? document.title : '');

  useEffect(() => {
    if (!cabinetId || !uid) return undefined;
    return NotificationService.subscribe({ cabinetId, uid, isArchitect }, setItems);
  }, [cabinetId, uid, isArchitect]);

  const unread = items.filter((n) => NotificationService.isUnread(n, uid));

  // Compteur dans le titre de l'onglet
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const base = baseTitle.current;
    document.title = unread.length ? `(${unread.length}) ${base}` : base;
    return () => { document.title = base; };
  }, [unread.length]);

  // Fermeture au clic extérieur / Échap
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const handleClick = (n) => {
    if (NotificationService.isUnread(n, uid)) NotificationService.markRead(n.id, uid);
    setOpen(false);
    if (n.projectId && onOpenProject) onOpenProject(n.projectId);
  };

  const list = (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <p className="font-semibold text-slate-800 text-sm">Notifications{unread.length ? <span className="ml-2 text-xs font-medium text-brand-700 bg-brand-50 px-2 py-0.5 rounded-full">{unread.length} non lue{unread.length > 1 ? 's' : ''}</span> : null}</p>
        <div className="flex items-center gap-1">
          {unread.length > 0 && (
            <button onClick={() => NotificationService.markAllRead(items, uid)} className="btn-ghost !min-h-[36px] px-2 text-xs text-slate-600" title="Tout marquer comme lu">
              <Check className="h-4 w-4" /> Tout lu
            </button>
          )}
          <button onClick={() => setOpen(false)} className="icon-btn sm:hidden" aria-label="Fermer"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <ul className="max-h-[70vh] sm:max-h-[420px] overflow-y-auto custom-scrollbar divide-y divide-slate-100">
        {items.length === 0 ? (
          <li className="px-4 py-8 text-center text-sm text-slate-500">Aucune notification pour le moment.</li>
        ) : items.map((n) => {
          const { Icon, cls } = ICONS[n.type] || ICONS.message;
          const isUnread = NotificationService.isUnread(n, uid);
          return (
            <li key={n.id}>
              <button
                onClick={() => handleClick(n)}
                className={`w-full text-left px-4 py-3 flex gap-3 items-start hover:bg-slate-50 transition-colors ${isUnread ? 'bg-brand-50/60' : ''}`}
              >
                <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cls}`}><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className={`text-xs uppercase tracking-wide ${isUnread ? 'text-brand-700 font-semibold' : 'text-slate-500'}`}>{NOTIF_LABELS[n.type] || n.type}</span>
                    <span className="text-[11px] text-slate-400 ml-auto whitespace-nowrap">{relative(n.createdAt)}</span>
                  </span>
                  <span className={`block text-sm truncate ${isUnread ? 'font-semibold text-slate-900' : 'text-slate-800'}`}>{n.title}</span>
                  {n.body && <span className="block text-xs text-slate-600 line-clamp-2 break-words">{n.body}</span>}
                  {n.fromName && n.type !== 'message' && <span className="block text-[11px] text-slate-400 mt-0.5">par {n.fromName}</span>}
                </span>
                {isUnread && <span className="mt-2 w-2 h-2 rounded-full bg-brand-600 flex-shrink-0" aria-hidden="true" />}
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="icon-btn relative"
        aria-label={`Notifications${unread.length ? ` (${unread.length} non lues)` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell className="h-5 w-5" />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {unread.length > 99 ? '99+' : unread.length}
          </span>
        )}
      </button>
      {open && (
        <>
          {/* Mobile : feuille en bas ; desktop : menu ancré */}
          <div className="fixed inset-0 bg-slate-900/40 z-50 sm:hidden" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="dialog"
            aria-label="Notifications"
            className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-card-hover sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:rounded-2xl sm:border sm:border-slate-200"
          >
            {list}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
